import type { CSSProperties } from 'react';
import type { BlockStyle } from '@/blocks/domain/types';
import { findMetricCompatibleFont } from '@/templates/domain/constants/metricCompatibleFonts';

/** Point size stored on the block, or undefined when the block still uses CSS pixels. */
export function measuredFontSizePt(style: Pick<BlockStyle, 'fontSizePt'>): number | undefined {
  const pt = style.fontSizePt;
  if (typeof pt !== 'number' || !Number.isFinite(pt) || pt <= 0) return undefined;
  return pt;
}

/**
 * CSS font-size. A measured point size is emitted as `Npt` so the preview does
 * not snap to a coarser pixel. Blocks without one keep the existing px number.
 */
export function blockFontSizeCss(style: Pick<BlockStyle, 'fontSize' | 'fontSizePt'>): CSSProperties['fontSize'] {
  const pt = measuredFontSizePt(style);
  if (pt !== undefined) return `${pt}pt`;
  return style.fontSize;
}

/** CSS font-weight. A numeric weight wins so ExtraBold (800) is not collapsed to 700. */
export function blockFontWeightCss(style: Pick<BlockStyle, 'fontWeight' | 'fontWeightValue'>): CSSProperties['fontWeight'] {
  const weight = style.fontWeightValue;
  if (typeof weight === 'number' && Number.isFinite(weight) && weight >= 100 && weight <= 900) return weight;
  return style.fontWeight;
}

/**
 * CSS size for a table header. Only a measured point size moves it; without one it is `undefined`, so the
 * header keeps its fixed 10px (8pt in the PDF) and existing documents do not reflow.
 */
export function measuredFontSizeCss(style: Pick<BlockStyle, 'fontSizePt'>): string | undefined {
  const pt = measuredFontSizePt(style);
  return pt === undefined ? undefined : `${pt}pt`;
}

/** The face the backend writes on every new block. It never meant "override the template font", so it keeps inheriting. */
const SEEDED_BLOCK_FONT_FAMILY = 'inter';

/** Face a block carries on its own, or undefined when it inherits the template base font. */
export function blockFontFamilyName(style: Pick<BlockStyle, 'fontFamily'>): string | undefined {
  const family = style.fontFamily?.trim();
  if (!family || family.toLowerCase() === SEEDED_BLOCK_FONT_FAMILY) return undefined;
  return family;
}

const SERIF_FAMILIES = new Set(['georgia', 'times new roman']);
const MONOSPACE_FAMILIES = new Set(['courier new']);

/**
 * CSS font stack for a named family: the family itself, the open stand-in with the same metrics when one exists (so a machine without the
 * face draws the same line breaks as the PDF, see metricCompatibleFonts), and a generic fallback so a missing face never falls to the
 * browser default serif.
 */
export function fontFamilyStackCss(family: string): string {
  const key = family.toLowerCase();
  let generic = 'sans-serif';
  if (SERIF_FAMILIES.has(key)) generic = 'serif';
  else if (MONOSPACE_FAMILIES.has(key)) generic = 'monospace';
  const substitute = findMetricCompatibleFont(family)?.substitute;
  return [JSON.stringify(family), substitute ? JSON.stringify(substitute) : null, generic].filter(Boolean).join(', ');
}

/** CSS font-family for a block that carries its own face; a block that inherits the template font carries none. */
export function blockFontFamilyCss(style: Pick<BlockStyle, 'fontFamily'>): string | undefined {
  const family = blockFontFamilyName(style);
  return family ? fontFamilyStackCss(family) : undefined;
}
